"use client";

import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CircularProgress from "@mui/material/CircularProgress";
import { useCart } from "@/app/_utils/providers/CartProvider";
import PromoService from "@/app/_services/PromoService";
import { CartError, type CartItem } from "@/app/_services/CartService";
import { heldMessage } from "@/app/_utils/availability";
import Header from "@/app/components/header";
import Footer from "@/app/components/footer";
import { Link as RouterLink } from "react-router-dom";
import MuiLink from "@mui/material/Link";
import { UtilityNoIndexHelmet } from "@/app/(public)/_seo/PublicLayoutMetadata";

const pageFont = '"Inter", "Helvetica Neue", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';

const accent = "#0a0a0a";
const inputFill = "#f4f4f5";
const disabledBtnBg = inputFill;
const disabledBtnFg = "#737373";
const disabledBtnBorder = "rgba(0,0,0,0.16)";
const chipBorder = "rgba(0,0,0,0.22)";
const promoInputText = "#525252";
const promoPlaceholder = "#737373";
/** Cart line quantity stepper */
const qtyStepperSize = 32;
const qtyStepperIcon = 18;
const qtyStepperRadius = "6px";
/** Desktop width for promo block + order summary so edges align */
const cartSummaryColumnSmPx = 400;
// Seats move fast in a rush; keep each line's sold-out or held state current while the cart is open.
const CART_POLL_MS = 5000;
const warnText = "#b45309";

type TicketLineState = "ok" | "short" | "held" | "sold_out";

function ticketLineState(item: CartItem): TicketLineState {
  const show = item.show;
  if (item.merch || !show || show.inventory_quantity == null) return "ok";
  const left = show.inventory_quantity;
  if (left <= 0) return (show.held_quantity ?? 0) > 0 ? "held" : "sold_out";
  return item.quantity > left ? "short" : "ok";
}

type PromoResult = {
  code: string;
  discount_percent: number;
  applicable_show_ids: string[]; // empty = all shows
};

export default function CartPage() {
  const { cart, loading, updateItem, removeItem, checkout, refreshCart } = useCart();

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (!document.hidden) refreshCart();
    }, CART_POLL_MS);
    return () => window.clearInterval(timer);
  }, [refreshCart]);

  const soldOutLines = (cart?.items ?? []).filter((item) => ticketLineState(item) === "sold_out").length;
  const everythingSoldOut = soldOutLines > 0 && soldOutLines === (cart?.items.length ?? 0);

  const [promoInput, setPromoInput] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState("");
  const [checkoutError, setCheckoutError] = useState("");
  const [checkoutErrorHeld, setCheckoutErrorHeld] = useState(false);
  const [itemErrors, setItemErrors] = useState<Record<string, string>>({});

  const changeQuantity = async (itemId: string, quantity: number) => {
    setItemErrors(({ [itemId]: _, ...rest }) => rest);
    try {
      await updateItem(itemId, quantity);
    } catch (err: any) {
      setItemErrors((prev) => ({ ...prev, [itemId]: err?.message || "Could not update quantity." }));
    }
  };
  const [appliedPromo, setAppliedPromo] = useState<PromoResult | null>(() => {
    try {
      const saved = sessionStorage.getItem("applied_promo");
      return saved ? (JSON.parse(saved) as PromoResult) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    try {
      if (appliedPromo) {
        sessionStorage.setItem("applied_promo", JSON.stringify(appliedPromo));
      } else {
        sessionStorage.removeItem("applied_promo");
      }
    } catch {}
  }, [appliedPromo]);

  const handleApplyPromo = async () => {
    if (promoLoading) return;
    const code = promoInput.trim();
    if (!code) return;
    if (appliedPromo && appliedPromo.code.toLowerCase() === code.toLowerCase()) {
      setPromoError("");
      return;
    }
    setPromoLoading(true);
    setPromoError("");
    setAppliedPromo(null);
    try {
      const result = await PromoService.validatePromoCodeAsync(code);
      if (result.error) {
        setPromoError(result.error);
      } else {
        setAppliedPromo(result as PromoResult);
      }
    } catch {
      setPromoError("Failed to validate promo code.");
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoInput("");
    setPromoError("");
  };

  if (loading && !cart) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: pageFont,
        }}
      >
        <CircularProgress sx={{ color: "#000" }} />
        <Typography sx={{ ml: 2, color: "#000", fontFamily: pageFont, fontSize: "1rem" }}>Loading your cart...</Typography>
      </Box>
    );
  }

  const subtotal = cart?.total_price || 0;

  // Compute which cart items the promo applies to and the discount amount
  const promoApplicableItems = appliedPromo
    ? (cart?.items ?? []).filter((item) => {
        if (!item.show || item.merch) return false;
        if (appliedPromo.applicable_show_ids.length === 0) return true;
        return appliedPromo.applicable_show_ids.includes(item.show.id);
      })
    : [];

  const discountAmount = promoApplicableItems.reduce((sum, item) => {
    return sum + (item.total_price * appliedPromo!.discount_percent) / 100;
  }, 0);

  const finalTotal = subtotal - discountAmount;

  return (
    <>
    <UtilityNoIndexHelmet title="Cart | Underground Overground Comedy" path="/cart" />
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#fff",
        color: "#000",
        fontFamily: pageFont,
        "& .MuiTypography-root": { fontFamily: pageFont },
        "& .MuiButton-root": { fontFamily: pageFont },
        "& .MuiChip-label": { fontFamily: pageFont },
        "& .MuiInputBase-input": { fontFamily: pageFont },
      }}
    >
      <Header />
      <Container maxWidth="md" sx={{ py: { xs: 4, sm: 6, md: 8 }, px: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
            mb: { xs: 3, sm: 4 },
            flexWrap: "wrap",
          }}
        >
          <Typography
            component="h1"
            sx={{
              m: 0,
              fontWeight: 700,
              fontFamily: pageFont,
              fontSize: { xs: "clamp(1.35rem, 5vw, 1.75rem)", sm: "1.85rem", md: "2rem" },
              lineHeight: 1.2,
              letterSpacing: "-0.02em",
              color: accent,
            }}
          >
            Your Cart
          </Typography>
          <MuiLink
            component={RouterLink}
            to="/tickets"
            underline="hover"
            sx={{
              fontFamily: pageFont,
              fontWeight: 600,
              fontSize: { xs: 14, sm: 15 },
              color: accent,
              flexShrink: 0,
            }}
          >
            Continue shopping
          </MuiLink>
        </Box>

        {!cart || cart.items.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography
              sx={{
                mb: 3,
                color: "rgba(0,0,0,0.6)",
                fontFamily: pageFont,
                fontWeight: 500,
                fontSize: { xs: "1rem", sm: "1.0625rem" },
                lineHeight: 1.6,
              }}
            >
              Your cart is empty
            </Typography>
            <Button
              disableElevation
              variant="contained"
              href="/tickets"
              sx={{
                backgroundColor: "#000",
                color: "#fff",
                fontFamily: pageFont,
                fontWeight: 700,
                fontSize: { xs: "1rem", sm: "1.0625rem" },
                letterSpacing: "0.02em",
                textTransform: "none",
                py: { xs: 1.75, sm: 2 },
                px: { xs: 3.5, sm: 4 },
                borderRadius: "6px",
                minHeight: { xs: 52, sm: 56 },
                boxSizing: "border-box",
                lineHeight: 1.2,
                "&:hover": { backgroundColor: "rgba(0,0,0,0.85)" },
              }}
            >
              Browse Shows
            </Button>
          </Box>
        ) : (
          <Box>
            {cart.items.map((item) => {
              const merch = item.merch;
              const show = item.show;
              if (!merch && !show) return null;
              // Promo codes are for tickets only.
              const isDiscounted =
                !merch &&
                !!show &&
                !!appliedPromo &&
                (appliedPromo.applicable_show_ids.length === 0 ||
                  appliedPromo.applicable_show_ids.includes(show.id));
              const itemDiscount = isDiscounted
                ? (item.total_price * appliedPromo!.discount_percent) / 100
                : 0;

              const maxQty = merch
                ? Math.min(10, merch.inventory_quantity ?? 0)
                : Math.min(
                    show?.max_quantity_per_order ?? 1,
                    show?.inventory_quantity ?? (show?.max_quantity_per_order ?? 1)
                  );
              const atMax = item.quantity >= maxQty;
              const lineState = ticketLineState(item);
              const left = show?.inventory_quantity ?? 0;
              const perOrder = show?.max_quantity_per_order ?? 1;
              // At the stepper's cap, say whether it's the per-order rule or simply all that's left.
              const capMessage = merch
                ? "Max limit reached"
                : show?.inventory_quantity != null && left < perOrder
                  ? `You have the last ${left} ticket${left === 1 ? "" : "s"}.${
                      (show?.held_quantity ?? 0) > 0 ? " The rest are held by other buyers." : ""
                    }`
                  : `Max ${perOrder} ticket${perOrder === 1 ? "" : "s"} per order.`;
              const lineTitle = merch ? merch.title : show?.title || "Item";
              const lineImage = (merch ? merch.img_url : show?.img_url) || "/placeholder.png";
              const linePrice = Number(merch ? merch.price : show?.ticket_price ?? 0);
              const fulfillmentLabel = merch
                ? merch.ships || !show
                  ? `${merch.variant_name ? `${merch.variant_name} · ` : ""}Ships to you`
                  : `${merch.variant_name ? `Size ${merch.variant_name} · ` : ""}Pick up at ${show.title}`
                : null;

              return (
                <Box
                  key={item.id}
                  sx={{
                    py: { xs: 2.5, sm: 3 },
                    borderBottom: "1px solid rgba(0,0,0,0.1)",
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "76px 1fr", sm: "88px 1fr" },
                      columnGap: { xs: 2, sm: 2.5 },
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 76, sm: 88 },
                        height: { xs: 76, sm: 88 },
                        flexShrink: 0,
                        borderRadius: 1,
                        overflow: "hidden",
                        bgcolor: inputFill,
                      }}
                    >
                      <Box
                        component="img"
                        src={lineImage}
                        alt={lineTitle}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    </Box>

                    <Box
                      sx={{
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        gap: 1.5,
                      }}
                    >
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: accent,
                                fontSize: { xs: "1.0625rem", sm: "1.2rem" },
                                lineHeight: 1.35,
                              }}
                            >
                              {lineTitle}
                            </Typography>
                            <Typography
                              sx={{
                                color: "rgba(0,0,0,0.55)",
                                fontSize: "1rem",
                                mt: 0.5,
                                fontWeight: 500,
                              }}
                            >
                              ${linePrice.toFixed(2)}
                            </Typography>
                            {fulfillmentLabel && (
                              <Typography sx={{ color: "rgba(0,0,0,0.55)", fontSize: "0.9375rem", mt: 0.5 }}>
                                {fulfillmentLabel}
                              </Typography>
                            )}
                            {!merch && show?.variant_name && (
                              <Typography sx={{ color: "rgba(0,0,0,0.55)", fontSize: "0.9375rem", mt: 0.5 }}>
                                Title: {show.variant_name}
                              </Typography>
                            )}
                            {isDiscounted && (
                              <Chip
                                label={`${appliedPromo!.discount_percent}% promo applied`}
                                size="small"
                                sx={{
                                  mt: 1,
                                  backgroundColor: "#f0fdf4",
                                  color: "#16a34a",
                                  fontWeight: 600,
                                  fontSize: 11,
                                }}
                              />
                            )}
                          </Box>
                          <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                            {isDiscounted ? (
                              <Box>
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    color: accent,
                                    textDecoration: "line-through",
                                    fontSize: "0.9rem",
                                  }}
                                >
                                  ${item.total_price.toFixed(2)}
                                </Typography>
                                <Typography sx={{ fontWeight: 700, color: "#16a34a", fontSize: "1.0625rem" }}>
                                  ${(item.total_price - itemDiscount).toFixed(2)}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography sx={{ fontWeight: 700, color: accent, fontSize: "1.0625rem" }}>
                                ${item.total_price.toFixed(2)}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1.5,
                          flexWrap: "wrap",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.5, sm: 2 }, flexWrap: "wrap" }}>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: 17,
                              color: accent,
                              lineHeight: 1,
                            }}
                          >
                            Quantity
                          </Typography>
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.75,
                            }}
                          >
                            <IconButton
                              disableRipple
                              disableFocusRipple
                              onClick={() => changeQuantity(item.id, item.quantity - 1)}
                              disabled={loading || item.quantity <= 1}
                              sx={{
                                width: qtyStepperSize,
                                height: qtyStepperSize,
                                minWidth: qtyStepperSize,
                                minHeight: qtyStepperSize,
                                p: 0,
                                borderRadius: qtyStepperRadius,
                                border: `1px solid ${chipBorder}`,
                                bgcolor: "#fff",
                                color: accent,
                                "&:hover": { bgcolor: "#fff" },
                                "&.Mui-disabled": {
                                  color: "rgba(0,0,0,0.28)",
                                  bgcolor: "#fff",
                                  border: `1px solid ${chipBorder}`,
                                },
                              }}
                            >
                              <RemoveIcon sx={{ fontSize: qtyStepperIcon }} />
                            </IconButton>
                            <Typography
                              component="span"
                              sx={{
                                minWidth: 28,
                                height: qtyStepperSize,
                                px: 0.5,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: accent,
                                fontSize: "0.9375rem",
                                fontWeight: 400,
                              }}
                            >
                              {item.quantity}
                            </Typography>
                            <IconButton
                              disableRipple
                              disableFocusRipple
                              onClick={() => changeQuantity(item.id, item.quantity + 1)}
                              disabled={loading || atMax}
                              sx={{
                                width: qtyStepperSize,
                                height: qtyStepperSize,
                                minWidth: qtyStepperSize,
                                minHeight: qtyStepperSize,
                                p: 0,
                                borderRadius: qtyStepperRadius,
                                border: `1px solid ${chipBorder}`,
                                bgcolor: "#fff",
                                color: accent,
                                "&:hover": { bgcolor: "#fff" },
                                "&.Mui-disabled": {
                                  color: "rgba(0,0,0,0.28)",
                                  bgcolor: "#fff",
                                  border: `1px solid ${chipBorder}`,
                                },
                              }}
                            >
                              <AddIcon sx={{ fontSize: qtyStepperIcon }} />
                            </IconButton>
                          </Box>
                        </Box>

                        <IconButton
                          disableRipple
                          disableFocusRipple
                          onClick={() => removeItem(item.id)}
                          disabled={loading}
                          aria-label="Remove item from cart"
                          sx={{
                            color: "#dc2626",
                            p: 1,
                            bgcolor: "transparent",
                            "&:hover": { bgcolor: "transparent", color: "#b91c1c" },
                            "&.Mui-disabled": { color: "rgba(220, 38, 38, 0.38)" },
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 24 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                  {itemErrors[item.id] ? (
                    <Typography role="alert" sx={{ fontSize: 12, color: "error.main", mt: 1, display: "block" }}>
                      {itemErrors[item.id]}
                    </Typography>
                  ) : lineState === "sold_out" ? (
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "error.main", mt: 1, display: "block" }}>
                      Sold out. Remove it to check out.
                    </Typography>
                  ) : lineState === "held" ? (
                    <Typography sx={{ fontSize: 13, color: warnText, mt: 1, display: "block" }}>
                      {heldMessage(show?.next_release_at)}
                    </Typography>
                  ) : lineState === "short" ? (
                    <Typography sx={{ fontSize: 13, color: warnText, mt: 1, display: "block" }}>
                      Only {left} ticket{left === 1 ? "" : "s"} left. Lower your quantity to check out.
                    </Typography>
                  ) : atMax && (
                    <Typography sx={{ fontSize: 13, color: merch ? "error.main" : "#525252", mt: 1, display: "block" }}>
                      {capMessage}
                    </Typography>
                  )}
                </Box>
              );
            })}

            {/* Promo Code Section */}
            <Box sx={{ mt: 4, display: "flex", justifyContent: { xs: "stretch", sm: "flex-end" }, width: "100%" }}>
              <Box sx={{ width: { xs: "100%", sm: cartSummaryColumnSmPx } }}>
                <Typography sx={{ fontWeight: 700, mb: 1.5, color: accent, fontSize: "1rem" }}>Promo Code</Typography>

                {appliedPromo ? (
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <CheckCircleOutlineIcon sx={{ color: "#16a34a", fontSize: 18 }} />
                      <Typography sx={{ fontWeight: 600, color: "#16a34a", fontSize: 13 }}>
                        {appliedPromo.code} — {appliedPromo.discount_percent}% off
                      </Typography>
                      <Button
                        disableElevation
                        size="small"
                        onClick={handleRemovePromo}
                        sx={{ ml: "auto", color: "rgba(0,0,0,0.5)", textTransform: "none", fontSize: 12, p: 0, minWidth: 0 }}
                      >
                        Remove
                      </Button>
                    </Box>
                    {appliedPromo.applicable_show_ids.length > 0 ? (
                      <Box>
                        <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.6)", mb: 1, fontSize: 13 }}>
                          Applies to:
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                          {promoApplicableItems.length > 0 ? (
                            promoApplicableItems.map((item) => (
                              <Chip
                                key={item.id}
                                label={item.show?.title}
                                size="small"
                                sx={{ backgroundColor: "#f0fdf4", color: "#16a34a", fontWeight: 600, fontSize: 13 }}
                              />
                            ))
                          ) : (
                            <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.5)", fontSize: 12 }}>
                              No eligible items in cart.
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.6)", fontSize: 12 }}>
                        Applies to all shows in your cart.
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Box>
                    {/* Control sizing mirrors the password unlock row on the product page
                        (48px tall input + button, 96px min button width). */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.25,
                        alignItems: "stretch",
                        flexDirection: { xs: "column", sm: "row" },
                      }}
                    >
                      <TextField
                        size="small"
                        placeholder="Enter promo code"
                        value={promoInput}
                        onChange={(e) => {
                          setPromoInput(e.target.value.toUpperCase());
                          setPromoError("");
                        }}
                        onKeyDown={(e) => e.key === "Enter" && handleApplyPromo()}
                        error={!!promoError}
                        sx={{
                          flex: 1,
                          "& .MuiInputBase-input": {
                            fontSize: 16,
                            py: "12px",
                            color: promoInputText,
                            "&::placeholder": {
                              color: promoPlaceholder,
                              opacity: 1,
                            },
                          },
                          "& .MuiOutlinedInput-root": {
                            minHeight: 48,
                            backgroundColor: "#fff",
                            borderRadius: 1,
                            "& fieldset": { borderColor: chipBorder },
                            "&:hover fieldset": { borderColor: "rgba(0,0,0,0.35)" },
                            "&.Mui-focused fieldset": { borderWidth: 1 },
                          },
                        }}
                      />
                      <Button
                        disableElevation
                        variant="contained"
                        onClick={handleApplyPromo}
                        disabled={!promoInput.trim()}
                        sx={{
                          alignSelf: "stretch",
                          backgroundColor: accent,
                          color: "#fff",
                          fontWeight: 400,
                          textTransform: "none",
                          whiteSpace: "nowrap",
                          fontSize: 16,
                          px: 2.5,
                          minWidth: { xs: "100%", sm: 96 },
                          minHeight: 48,
                          borderRadius: 1,
                          boxShadow: "none",
                          border: "none",
                          "&:hover": { backgroundColor: "#262626", boxShadow: "none", border: "none" },
                          "&.Mui-disabled": {
                            backgroundColor: disabledBtnBg,
                            color: disabledBtnFg,
                            border: "none",
                            opacity: 1,
                          },
                        }}
                      >
                        {promoLoading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Apply"}
                      </Button>
                    </Box>
                    {promoError && (
                      <Typography sx={{ fontSize: 12, color: "#ef4444", mt: 0.75 }}>{promoError}</Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Order Summary — same sm width as promo row so Checkout spans input+Apply */}
            <Box
              sx={{
                mt: 4,
                display: "flex",
                justifyContent: { xs: "stretch", sm: "flex-end" },
                width: "100%",
              }}
            >
              <Box
                sx={{
                  width: { xs: "100%", sm: cartSummaryColumnSmPx },
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    gap: 2,
                    width: "100%",
                  }}
                >
                  <Typography sx={{ color: "rgba(0,0,0,0.55)", fontSize: "1.0625rem" }}>Subtotal</Typography>
                  <Typography sx={{ fontWeight: 700, color: accent, fontSize: "1.0625rem" }}>${subtotal.toFixed(2)}</Typography>
                </Box>
                {appliedPromo && discountAmount > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                      gap: 2,
                      width: "100%",
                    }}
                  >
                    <Typography sx={{ color: "#16a34a", fontSize: "1.0625rem" }}>
                      Promo ({appliedPromo.code})
                    </Typography>
                    <Typography sx={{ fontWeight: 700, color: "#16a34a", fontSize: "1.0625rem" }}>
                      −${discountAmount.toFixed(2)}
                    </Typography>
                  </Box>
                )}
                {appliedPromo && discountAmount === 0 && (
                  <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.5)" }}>
                    No eligible items in cart for this promo.
                  </Typography>
                )}
                {appliedPromo && discountAmount > 0 && (
                  <>
                    <Divider sx={{ width: "100%", borderColor: "rgba(0,0,0,0.1)" }} />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        gap: 2,
                        width: "100%",
                      }}
                    >
                      <Typography sx={{ color: "rgba(0,0,0,0.55)", fontWeight: 700, fontSize: "1.25rem" }}>Total</Typography>
                      <Typography sx={{ fontWeight: 700, color: accent, fontSize: "1.25rem" }}>${finalTotal.toFixed(2)}</Typography>
                    </Box>
                  </>
                )}
                {!appliedPromo && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                      gap: 2,
                      width: "100%",
                      mt: 0.5,
                    }}
                  >
                    <Typography sx={{ color: "rgba(0,0,0,0.55)", fontWeight: 700, fontSize: "1.25rem" }}>Total</Typography>
                    <Typography sx={{ fontWeight: 700, color: accent, fontSize: "1.25rem" }}>${subtotal.toFixed(2)}</Typography>
                  </Box>
                )}
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "rgba(0,0,0,0.45)",
                    textAlign: { xs: "center", sm: "right" },
                    width: "100%",
                    lineHeight: 1.5,
                    px: { xs: 0.5, sm: 0 },
                  }}
                >
                  Taxes, discounts and shipping calculated at checkout.
                </Typography>
                <Button
                  disableElevation
                  variant="contained"
                  onClick={async () => {
                    setCheckoutError("");
                    try {
                      await checkout(appliedPromo?.code);
                    } catch (err: any) {
                      setCheckoutError(err?.message || "Could not start checkout. Please try again.");
                      setCheckoutErrorHeld(err instanceof CartError && err.code === "held");
                    }
                  }}
                  disabled={loading || soldOutLines > 0}
                  sx={{
                    mt: 1,
                    backgroundColor: accent,
                    color: "#fff",
                    px: 3,
                    py: 2,
                    minHeight: { xs: 56, sm: 60 },
                    borderRadius: 1,
                    fontWeight: 700,
                    fontSize: { xs: "1.0625rem", sm: "1.125rem" },
                    textTransform: "none",
                    boxShadow: "none",
                    border: `1px solid ${accent}`,
                    width: "100%",
                    "&:hover": { backgroundColor: "#262626", boxShadow: "none", borderColor: "#262626" },
                    "&.Mui-disabled": {
                      backgroundColor: disabledBtnBg,
                      color: disabledBtnFg,
                      border: `1px solid ${disabledBtnBorder}`,
                      opacity: 1,
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={26} sx={{ color: accent }} />
                  ) : everythingSoldOut ? (
                    "Sold out"
                  ) : soldOutLines > 0 ? (
                    "Remove sold-out tickets to check out"
                  ) : (
                    "Checkout"
                  )}
                </Button>
                {checkoutError && (
                  <Typography role="alert" sx={{ fontSize: 14, color: checkoutErrorHeld ? "#d97706" : "#ef4444", width: "100%", textAlign: { xs: "center", sm: "right" } }}>
                    {checkoutError}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Container>
      <Footer />
    </Box>
    </>
  );
}
